import { useId, useState } from 'react';
import type { Overrun, ScheduledUnit } from '../../core/schedule';
import { plural } from './UnitRow';

/**
 * R16: the plan runs past the last day. Names the units and weeks over, then lets the teacher
 * shorten or drop one of the later units, or leave it. Nothing changes until they choose.
 */
export function OverrunPanel({ overrun, units, busy = false, onShorten, onDrop, onLeave }: {
  overrun: Overrun; units: ScheduledUnit[]; busy?: boolean;
  onShorten(unitId: string, by: number): void; onDrop(unitId: string): void; onLeave(): void;
}) {
  const titleId = useId();
  const named = new Set(overrun.units.map(u => u.id));
  const suggested = () => [...units].reverse().find(u => named.has(u.id))?.id ?? units.at(-1)?.id ?? '';
  const [chosenId, setChosenId] = useState(suggested);
  const [confirming, setConfirming] = useState(false);
  // The chosen unit was dropped or deleted: choose afresh, and never carry a confirmation over
  // to whichever unit would otherwise stand in for it.
  if (chosenId && !units.some(u => u.id === chosenId)) {
    setChosenId(suggested());
    setConfirming(false);
  }
  const chosen = units.find(u => u.id === chosenId) ?? units.at(-1);
  // Take back as many weeks as the plan is over, keeping the unit at one week or more.
  const by = chosen ? Math.max(1, Math.min(overrun.weeksOver, chosen.weeks - 1)) : 1;

  return <section className="overrun-panel" aria-labelledby={titleId}>
    <h2 id={titleId}>Your plan now runs past the last day</h2>
    <ul className="overrun-units">{overrun.units.map(u => <li key={u.id}>{u.name}: {plural(u.weeksOver, 'week')} over</li>)}</ul>
    {chosen
      ? <>
          <p className="muted">Shorten a later unit or drop one. Nothing changes until you choose.</p>
          <label className="overrun-field">Unit to change
            <select value={chosen.id} disabled={busy} onChange={e => { setChosenId(e.target.value); setConfirming(false); }}>
              {units.map(u => <option key={u.id} value={u.id}>{u.name} ({plural(u.weeks, 'week')})</option>)}
            </select>
          </label>
          {chosen.weeks <= 1 && <p className="muted overrun-note">{chosen.name} is already one week long.</p>}
          {confirming
            ? <div className="overrun-confirm" role="group" aria-label={`Drop ${chosen.name}?`}>
                <p>Drop <strong>{chosen.name}</strong>? Its standards move to Not yet placed.</p>
                <div className="page-actions">
                  <button className="primary destructive" type="button" disabled={busy} onClick={() => { setConfirming(false); onDrop(chosen.id); }}>Yes, drop {chosen.name}</button>
                  <button className="secondary" type="button" disabled={busy} onClick={() => setConfirming(false)}>Keep it</button>
                </div>
              </div>
            : <div className="page-actions">
                <button className="primary" type="button" disabled={busy || chosen.weeks <= 1} onClick={() => onShorten(chosen.id, by)}>Take {plural(by, 'week')} off {chosen.name}</button>
                <button className="secondary" type="button" disabled={busy} onClick={() => setConfirming(true)}>Drop {chosen.name}</button>
                <button className="text-button" type="button" onClick={onLeave}>Leave for now</button>
              </div>}
        </>
      : <>
          <p className="muted">There is no later unit to shorten or drop. Change the weeks in the plan editor.</p>
          <div className="page-actions"><button className="text-button" type="button" onClick={onLeave}>Leave for now</button></div>
        </>}
  </section>;
}
